import React, { FC, useCallback, useEffect, useState } from 'react'
import ApprovalListItem from '../../components/Approval/ListItem'
import PostConversation from '../../components/PostCreation/PostConversation'
import { useDispatch, useSelector } from '../../store'
import {
  approveCurrentRoundThunk,
  unApproveCurrentRoundThunk,
  getPostApprovalInfoThunk,
  showAllPostSocialNoteThunk,
  startNextApprovalRoundThunk
} from '../../store/managePost/thunks'
import { getPostsForApprovalThunk, getApprovedPostsThunk } from '../../store/project/thunks'
import { TApproveCurrentRoundPayload, TUnApproveCurrentRoundPayload } from '../../store/managePost/newTypes'

import './ApprovalItem.scss'
import { setPostToApproveAction, setSelectedPostSocialIdAction } from '../../store/managePost/slice'
import Preloader from '../../components/Preloader'
import { hideGlobalPreloaderAction, showGlobalPreloaderAction } from '../../store/preloader/slice'
import { selectNotApprovedPostsWithoutRequireRevision, selectApprovedPosts } from '../../selectors/PostSelector'
import { SOCIAL_NETWORK } from '../../models/post'
import { useParams } from 'react-router-dom'
import { setHeaderView, showApprovalListAction } from '../../store/application/slice'
import { HEADER_VIEW, APPROVAL_LIST_TYPE } from '../../store/application/types'

interface AppProps { }

const ApprovalItem: FC<AppProps> = ({ children }) => {
  const dispatch = useDispatch()

  const {
    postToApprove,
    location,
    project,
    selectedPosts,
    latestSchedule,
    postSocials,
    approvalPlan,
    postSocialsNotes,
    postIdAfterSetToStore,
    approvalListType
  } = useSelector(({ agency, router, auth, project, managePost, approvalModal , app}) => ({
    location: router.location,
    project: project.currentProject,
    selectedPosts: approvalModal.posts_for_approval,
    postToApprove: managePost.post,
    user: auth.user,
    latestSchedule: managePost.latestSchedule,
    postSocials: managePost.postSocials,
    approvalPlan: managePost.approval.approval_plan,
    postSocialsNotes: managePost.postSocialNotes,
    postIdAfterSetToStore: managePost.post.id,
    approvalListType: app.approvalListType
  }))
  const postsToApprove = useSelector(selectNotApprovedPostsWithoutRequireRevision)
  const postToUnapprove = useSelector(selectApprovedPosts)

  const postId = (location as any).query.postId
  const { social }: { social: SOCIAL_NETWORK } = useParams()

  useEffect(() => {
    const path = location.pathname.split('/')[2]
    dispatch(showApprovalListAction(path === 'unapprove'?APPROVAL_LIST_TYPE.APPROVED:APPROVAL_LIST_TYPE.PENDING))
    dispatch(setHeaderView(HEADER_VIEW.APPROVAL_ITEM))
  }, [])

  useEffect(() => {
    const postToApprove = postsToApprove.find(
      (postToApprove) => postToApprove.id === Number(postId)
    )
    const path = location.pathname.split('/')[2]
    if (postToApprove && path !== 'unapprove') {
      dispatch(setPostToApproveAction(postToApprove))
    }
  }, [postId, postsToApprove])

  useEffect(() => {
    const activePostSocial = postSocials.find((socialPost) => socialPost.social_network === social)
    dispatch(setSelectedPostSocialIdAction(activePostSocial ? activePostSocial.id : 0))
  }, [social, postSocials])

  useEffect(() => {
    const getPostsForApprove = async () => {
      dispatch(showGlobalPreloaderAction())
      await dispatch(getPostsForApprovalThunk())
      dispatch(hideGlobalPreloaderAction())
    }
    const getApprovedPostList = async () => {
      dispatch(showGlobalPreloaderAction())
      await dispatch(getApprovedPostsThunk())
      dispatch(hideGlobalPreloaderAction())
    }

    if (project && project.id && !postsToApprove.length &&  approvalListType === APPROVAL_LIST_TYPE.PENDING) getPostsForApprove()
    if (project && project.id &&  !postToUnapprove.length &&  approvalListType === APPROVAL_LIST_TYPE.APPROVED) getApprovedPostList()
  }, [ project, postsToApprove, postToUnapprove, approvalListType])

  useEffect(() => {
    if (postIdAfterSetToStore && postSocials.length) {
      const getAllPostSocialsNotes = async () => {
        return postSocials.map(async ({ id }) => {
          if (!postSocialsNotes[id]) await dispatch(showAllPostSocialNoteThunk(id))
        })
      }
      getAllPostSocialsNotes()
    }
  }, [postIdAfterSetToStore, postSocials, postSocialsNotes])

  const startNextApprovalRound = useCallback(
    (data: TApproveCurrentRoundPayload) => dispatch(startNextApprovalRoundThunk(data)),
    [dispatch, startNextApprovalRoundThunk]
  )

  useEffect(() => {
    const getApprovalPlan = async () => {
      await dispatch(getPostApprovalInfoThunk(postToApprove.id))
    }

    if (postToApprove.id ) {
      getApprovalPlan()
    }
  }, [postToApprove.id])

  const approveCurrentRound = useCallback(
    (data: TApproveCurrentRoundPayload) => dispatch(approveCurrentRoundThunk(data)),
    [dispatch, approveCurrentRoundThunk]
  )

  const setSelectedPosts = (posts: any) => { }

  const setOrRemoveSelectedPosts = (post: any) => {
    const isFindedEvent = selectedPosts.find((selectedPost) => selectedPost.id === post.id)

    if (!isFindedEvent) {
      setSelectedPosts([...selectedPosts, post])
    } else {
      setSelectedPosts(selectedPosts.filter((selectedPost) => selectedPost.id !== post.id))
    }
  }

  const approvalListItemProps = {
    approveCurrentRound,
    startNextApprovalRound,
    setCurrectPostToApproval: () => { },
    setSelectedPosts: setOrRemoveSelectedPosts,
    isSinglePost: true,
    project,
    socials: project.socials
  }

  return (
    <>
      <div className='approvals'>
        <div className='approvals__items-wrapper'>
          {!!postToApprove.id ? (
            <div
              className='approvals__items-wrapper__item-wrapper'
              key={`approval-list-item-${postToApprove.id}`}
            >
              <ApprovalListItem
                {...approvalListItemProps}
                latestSchedule={latestSchedule}
                postSocials={postSocials}
                post={postToApprove}
              />
            </div>
          ) : (
            <div className='approvals__items-wrapper__preloader-wrapper'>
              <Preloader />
            </div>
          )}
        </div>

        <div className='approvals__notes'>
          <PostConversation />
        </div>
      </div>
    </>
  )
}

export default ApprovalItem
